import { Deal, ApplyTo } from '../../../models/deals'
import { DealReport } from '../../../models/deals_reports'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextInput } from '../../FormUtils/TextInput'
import { useEffect, useState } from 'react'
import Select from '../../FormUtils/Select'
import apiService from '../../../services/api'
import { toast } from 'react-toastify'
import { featureFlagService } from 'utils/featureFlagService'
import { ChevronDown, ChevronUp } from 'lucide-react'
import {
  checkDoorSelectionRules,
  selectDoorsStepFormSchema,
} from '../CreateDealModal/SelectDoorsStep'
import {
  useProductVariantSearch,
  type SelectedVariant,
} from 'components/UI/ProductSearchModal'
import { MobileModal } from 'components/UI/Modal/MobileModal'
import FbButton from 'components/FbUI/FbButton'
import { RadioIconSelect } from 'components/FormUtils/RadioIconSelect'
import { Building2, MapPin } from 'lucide-react'
import { FbLink } from 'components/FbUI/FbLink'
import { MobileSelectLocationsModal } from './MobileSelectLocationsModal'
import { MobileConfirmationModal } from 'components/MobileConfirmationModal'
import { useQueryClient } from '@tanstack/react-query'

type DealOrReport = Deal | DealReport

function isDealReport(deal: DealOrReport): deal is DealReport {
  return 'cc_doors_cnt' in deal || 'sales_stage_id' in deal
}

function getDealFields(deal: DealOrReport) {
  if (isDealReport(deal)) {
    return {
      name: deal.deal_name,
      accountOwner: deal.account_owner_id,
      salesStage: deal.sales_stage_id,
      originCampaign: deal.origin_campaign_id,
      contactCompanyId: deal.cc_id,
      contactCompanyName: deal.cc_overriden_name || deal.cc_company_name,
      applyTo: deal.apply_to as ApplyTo | undefined,
    }
  }
  return {
    name: deal.name,
    accountOwner: deal.account_owner,
    salesStage: deal.sales_stage,
    originCampaign: deal.origin_campaign,
    contactCompanyId: deal.contact_company?.id,
    contactCompanyName: deal.contact_company?.name,
    applyTo: deal.apply_to,
  }
}

interface EditDealModalProps {
  show: boolean
  onClose: () => void
  deal?: DealOrReport
  dealsTableQueryKey: string[]
}

const editDealSchema = z
  .object({
    name: z
      .union([
        z.string().max(100, 'Deal name must be 100 characters or less'),
        z.null(),
        z.undefined(),
      ])
      .optional()
      .nullable(),
    monthly_volume_override: z.union([
      z.number().min(0).optional().nullable(),
      z.literal(''),
    ]),
    monthly_revenue_override: z.union([
      z.number().min(0).optional().nullable(),
      z.literal(''),
    ]),
    close_date_override: z.string().optional().nullable(),
    account_owner: z.number().optional().nullable(),
    sales_stage: z.number().optional().nullable(),
    product_variant_id: z.number().optional().nullable(),
    notes: z.string().optional().nullable(),
    attachments: z.array(z.any()).optional().nullable(),
    origin_campaign_id: z.number().optional().nullable(),
    product_id: z.number(),
  })
  .merge(selectDoorsStepFormSchema)
  .superRefine((v, ctx) => {
    checkDoorSelectionRules(v, ctx)
  })
type EditDealFormValues = z.infer<typeof editDealSchema>

export function MobileEditDealModal(props: EditDealModalProps) {
  const queryClient = useQueryClient()
  const featureFlag = featureFlagService()
  const api = apiService()
  const [overridesExpanded, setOverridesExpanded] = useState(false)
  const [showLocationModal, setShowLocationModal] = useState<boolean>(false)

  const { data: stageOptions } = api.useGetSalesStageOptions()
  const { data: userOptions } = api.useGetUserOptions({ onlyActive: true })
  const { data: productOptions } = api.useGetProductsOptions()

  const { data: campaignOptions } = api.useGetCampaignsOptions()
  const [, setLoading] = useState(false)
  const [, setInitialDoorIds] = useState<number[]>([])
  const [, setSelectedVariant] = useState<SelectedVariant | null>(null)
  const { data: products } = api.useGetProducts()
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false)

  const dealFields = props.deal ? getDealFields(props.deal) : null

  const handleClose = () => {
    setInitialDoorIds([])
    props.onClose()
  }
  // SelectDoorStepWithControl requires the form to be typed
  const form = useForm<EditDealFormValues>({
    resolver: zodResolver(editDealSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
  })

  const search = useProductVariantSearch({
    mode: 'variants',
    initialFilters: form?.watch('product_id')
      ? { productId: form?.watch('product_id') }
      : undefined,
    pageSize: 50,
    enabled: true, // Only fetch when modal is open
  })

  const { setValue, watch } = form

  useEffect(() => {
    if (!props.deal || !dealFields) return
    form.reset({
      name: dealFields.name || '',
      product_id: props.deal.product_id,
      product_variant_id: props.deal?.product_variant_id,
      monthly_volume_override: props.deal?.monthly_volume_override,
      monthly_revenue_override: props.deal?.monthly_revenue_override,
      close_date_override: props.deal?.close_date_override,
      account_owner: dealFields.accountOwner ?? undefined,
      sales_stage: dealFields.salesStage ?? undefined,
      notes: '',
      attachments: [],
      origin_campaign_id: dealFields.originCampaign ?? undefined,
      apply_to: dealFields.applyTo,
    })
  }, [props.deal, props.show])

  const onSubmit = form.handleSubmit(async (values) => {
    if (!props.deal) return

    const body = {
      name: values.name || undefined,
      product_id: values.product_id,
      product_variant_id: values.product_variant_id ?? undefined,
      monthly_volume_override: values.monthly_volume_override || null,
      monthly_revenue_override: values.monthly_revenue_override || null,
      close_date_override: values.close_date_override ?? undefined,
      account_owner: values.account_owner ?? undefined,
      sales_stage: values.sales_stage ?? undefined,
      notes: values.notes ?? undefined,
      attachments: values.attachments ?? undefined,
      origin_campaign_id: values.origin_campaign_id
        ? values.origin_campaign_id.toString()
        : undefined,
      apply_to: values.apply_to,
      door_ids: values.door_ids,
    }

    setLoading(true)
    await api.editDeal(props.deal.id, body)
    toast.success('Deal updated successfully')
    setLoading(false)

    handleClose()

    await new Promise((resolve) => setTimeout(resolve, 1000))
    await queryClient.refetchQueries({ queryKey: props.dealsTableQueryKey })
  })

  useEffect(() => {
    if (props.deal && dealFields?.applyTo === 'select_locations') {
      api.getSelectedDoorIdsForDeal(props.deal.id).then((result) => {
        setInitialDoorIds(result.ids ?? [])
        form.reset({
          door_ids: result.ids ?? [],
        })
      })
    }
  }, [props.deal, form.reset, setInitialDoorIds])

  // Initialize selected variant from existing form values
  useEffect(() => {
    if (!props.deal || !products) return

    const variantId = props.deal.product_variant_id
    if (variantId) {
      // Find the product for this deal
      const productId = props.deal.product_id
      const product = products.find((p) => p.id === productId)

      if (product?.variants) {
        // Find the variant details
        const variant = product.variants.find((v) => v.id === variantId)
        if (variant) {
          setSelectedVariant({
            id: variant.id!,
            name: variant.name,
            format_pack_size: variant.format_pack_size || null,
            sku: variant.sku || '',
            gtin: variant.gtin || null,
            product_id: product.id,
            product_name: product.name,
            distributor_codes: variant.distributor_codes || [],
          })
        }
      }
    }
  }, [props.deal, products])

  const applyTo = watch('apply_to')
  const doorIds = watch('door_ids')

  useEffect(() => {
    if (applyTo === 'select_locations') {
      setShowLocationModal(true)
    }
  }, [applyTo])

  if (showLocationModal && dealFields?.contactCompanyId) {
    return (
      <MobileSelectLocationsModal
        initialDoorIds={doorIds || []}
        chainProxyId={dealFields.contactCompanyId}
        onCancel={() => {
          setShowLocationModal(false)
        }}
        onSubmit={(doorIds: number[]) => {
          setValue('door_ids', doorIds)
          setShowLocationModal(false)
        }}
      />
    )
  }

  if (showDeleteConfirmationModal) {
    return (
      <MobileConfirmationModal
        title="Delete this Deal?"
        message="This action cannot be undone."
        onClose={() => setShowDeleteConfirmationModal(false)}
        onConfirm={async () => {
          if (props.deal) {
            await api.deleteDeal(props.deal.id)
            setInitialDoorIds([])
            props.onClose()
            queryClient.invalidateQueries()
            setShowDeleteConfirmationModal(false)
          }
        }}
      />
    )
  }

  return (
    <MobileModal
      header={
        <div className="p-2">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Edit A Deal
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {dealFields?.name}
          </h1>
          <div className="text-xs text-fb-green-800 mt-2">
            <FbLink to={`${dealFields?.contactCompanyId}`}>
              {dealFields?.contactCompanyName}
            </FbLink>
          </div>
        </div>
      }
      footer={
        <div>
          <div className="flex gap-4 p-2 justify-between">
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="secondary"
                onClick={handleClose}
              >
                Cancel
              </FbButton>
            </div>
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="primary"
                onClick={onSubmit}
              >
                Save
              </FbButton>
            </div>
          </div>
          <div className="flex justify-center">
            {props.deal && (
              <FbLink
                onClick={(e) => {
                  e.preventDefault()
                  setShowDeleteConfirmationModal(true)
                }}
                style={{ fontSize: 10 }}
                className="text-danger"
                to="#"
              >
                Delete Deal
              </FbLink>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-6 w-full p-2">
        <div>
          <TextInput
            control={form.control}
            name="name"
            label="Deal Name"
            placeholder={dealFields?.contactCompanyName || 'Company Name'}
            optional={true}
            maxLength={100}
          />
        </div>

        <div>
          <Select
            label={'Product'}
            control={form.control}
            name={`product_id`}
            options={productOptions}
            placeholder="Select..."
            className="mt-1"
            isClearable={false}
          />
        </div>

        <div>
          <Select
            label={'Product Variant'}
            control={form.control}
            name={`product_variant_id`}
            options={search.variants?.map((v) => ({
              value: v?.id,
              label: v?.name,
            }))}
            placeholder="Select Variant"
            className="mt-1"
            disabled={!form.watch('product_id')}
            optional
          />
        </div>

        {/* Sales Stage, Account Owner, and Origin Campaign Row */}
        <div className="flex flex-col gap-2">
          <Select
            label={'Sales Stage'}
            control={form.control}
            name={`sales_stage`}
            options={stageOptions}
            placeholder="Target"
            className="mt-1"
          />
          <Select
            label={'Account Owner'}
            control={form.control}
            name={`account_owner`}
            options={userOptions}
            placeholder="Select..."
            className="mt-1"
          />
          {featureFlag.enableCampaigns ? (
            <Select
              label={'Origin Campaign'}
              control={form.control}
              name={`origin_campaign_id`}
              options={campaignOptions}
              placeholder="Select..."
              className="mt-1"
            />
          ) : (
            <div className="h-0" />
          )}
        </div>

        {/* Deal Overrides Section - Collapsible */}
        <div className="bg-[#f4f7f5] rounded-lg p-4 mb-4">
          <button
            type="button"
            onClick={() => setOverridesExpanded(!overridesExpanded)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-medium text-gray-900">
                Deal Overrides
              </h4>
              <span className="bg-[#d5dcd9] text-[#6b7280] text-xs px-2 py-1 rounded-full">
                Optional
              </span>
            </div>
            {overridesExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </button>

          {overridesExpanded && (
            <div className="mt-4 space-y-4">
              <TextInput
                control={form.control}
                label={'Est. Monthly Volume'}
                name={`monthly_volume_override`}
                type="number"
                placeholder="2000"
                optional={true}
              />
              <TextInput
                control={form.control}
                label={'Est. Monthly Revenue'}
                name={`monthly_revenue_override`}
                type="number"
                placeholder="$30,000"
                optional={true}
              />
              <TextInput
                control={form.control}
                name={`close_date_override`}
                label={'Est. Close Date'}
                type="date"
                optional={true}
              />
            </div>
          )}
        </div>
        {props.deal && (
          <>
            <div className="border border-gray-400 rounded-md p-3 flex flex-col gap-2">
              <div>Apply this deal to:</div>

              <RadioIconSelect
                name="apply_to"
                control={form.control}
                options={[
                  {
                    label: 'Entire Company',
                    key: 'entire_company',
                    icon: Building2,
                  },
                  {
                    label: doorIds?.length
                      ? `Select Locations (${doorIds.length})`
                      : 'Select Locations',
                    key: 'select_locations',
                    icon: MapPin,
                    onClick: () => setShowLocationModal(true),
                  },
                ]}
              />
            </div>
          </>
        )}
      </div>
    </MobileModal>
  )
}
